<!-- This component loads a Tix iframe and retrieves the Tix user and basket state from it -->

<template>
  <div>
    <!--
      Server-rendered on purpose, so the browser starts fetching it while the page is still parsing
      instead of after hydration. That means its load event can fire before `@load` is attached, so
      never make that event the only trigger for the session request: the handler below asks on mount
      as well, and between the two the request goes out whichever side of hydration the iframe lands.
      Leaving it to `@load` alone is what leaves a signed-in visitor looking logged out.
    -->
    <iframe ref="tixIframe" class="hidden" :src="tixIframeUrl" @load="onLoad"></iframe>
  </div>
</template>

<script setup lang="ts">
const { tixIframeUrl, tixUpdate } = setupTix()
const tixStore = useTixStore()
// A template ref holds null until the element is mounted, and again after it unmounts.
const tixIframe = ref<HTMLIFrameElement | null>(null)

watch(tixIframe, (node) => {
  tixStore.setIframe(node)
})

const responseListener = (messageEvent: MessageEvent) => {
  tixStore.response({ messageEvent })
}

const fetchSession = async () => {
  await tixStore
    .call({ call: 'GetSession', reload: false })
    .then((data) => {
      tixUpdate(data)
    })
    .catch((e) => {
      console.error('Error calling GetSession', e)
    })
}

tixStore.setCallback(fetchSession)

// The iframe's load event carries nothing the callbacks use, so it is deliberately not forwarded.
// It runs whatever callback is current, which other callers set through `call({ reload: true })`.
const onLoad = () => {
  tixStore.callback()
}

// Make sure that the event listener is also removed, otherwise they will accumulate
onMounted(async () => {
  window.addEventListener('message', responseListener)

  /*
   * The other half of the pair described in the template: by the time hydration gets here the iframe
   * may already have loaded, with its load event long gone, so ask for the session from this side
   * too. The store needs the element before it can post, and the watcher above only assigns it on
   * the next flush, so set it here and wait for that tick first. A request into an iframe that is
   * not ready yet is discarded, and `@load` then covers it, which is why this failure is silent.
   */
  tixStore.setIframe(tixIframe.value)
  await nextTick()
  tixStore
    .call({ call: 'GetSession', reload: false })
    .then((data) => tixUpdate(data))
    .catch(() => {})
})
onUnmounted(() => window.removeEventListener('message', responseListener))
</script>
